import React, { useState, useRef, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Send, Loader2, Bot, User, CheckCircle2, X, Sparkles, Mic, Plus, History } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Aaj ka performance kya raha?",
  "Kitne overdue follow-ups hain?",
  "Top hot leads kaun hain?",
  "Rahul ko Qualified stage me daal do",
  "Rahul ka follow-up kal ke liye set karo",
  "Rahul ke note me likho: broker interest high hai",
];

const WELCOME = { role: "assistant", text: "Salaam! 👋 Main aapka Fateh FX assistant hun. Leads, signals, ya aaj ke performance ke baare me poochho — ya command do jaise \"Rahul ko Qualified me move karo\", \"us lead ka follow-up kal set karo\", ya \"send today's performance to free channel\". Mic dabake bol bhi sakte ho." };
const SESSION_KEY = "fatehfx_assistant_session";
const SpeechRec = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function Assistant() {
  const [msgs, setMsgs] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || `sess-${Date.now()}`);
  const [recording, setRecording] = useState(false);
  const [lang, setLang] = useState("en-IN");
  const recRef = useRef(null);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const loadSessions = useCallback(async () => {
    try { const { data } = await client.get("/assistant/sessions"); setSessions(data.sessions || []); } catch {}
  }, []);

  const loadHistory = useCallback(async (sid) => {
    try {
      const { data } = await client.get("/assistant/history", { params: { session_id: sid } });
      const rows = (data.messages || []).map((m) => ({ role: m.role, text: m.text }));
      setMsgs(rows.length ? rows : [WELCOME]);
    } catch { setMsgs([WELCOME]); }
  }, []);

  useEffect(() => {
    localStorage.setItem(SESSION_KEY, sessionId);
    loadHistory(sessionId);
    loadSessions();
  }, [sessionId, loadHistory, loadSessions]);

  const newChat = () => {
    const sid = `sess-${Date.now()}`;
    setSessionId(sid); setMsgs([WELCOME]); setShowSessions(false);
  };

  const switchSession = (sid) => { setShowSessions(false); setSessionId(sid); };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await client.post("/assistant/chat", { message: q, session_id: sessionId });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, action: data.action }]);
      loadSessions();
    } catch (e) { setMsgs((m) => [...m, { role: "assistant", text: "Sorry, kuch galat ho gaya. Dubara try karo." }]); }
    setLoading(false);
  };

  const confirm = async (idx, action) => {
    setLoading(true);
    try {
      const { data } = await client.post("/assistant/chat", { confirm_action: action, session_id: sessionId });
      setMsgs((m) => m.map((msg, i) => i === idx ? { ...msg, action: null, done: true } : msg).concat([{ role: "assistant", text: data.reply }]));
    } catch { toast.error("Action failed"); }
    setLoading(false);
  };

  const cancel = (idx) => setMsgs((m) => m.map((msg, i) => i === idx ? { ...msg, action: null, text: msg.text + "\n\n(cancelled)" } : msg));

  const toggleMic = () => {
    if (!SpeechRec) { toast.error("Voice input is Chrome/Edge me supported hai."); return; }
    if (recording) { recRef.current?.stop(); return; }
    const rec = new SpeechRec();
    rec.lang = lang; rec.interimResults = true; rec.continuous = false;
    rec.onresult = (e) => {
      let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      setInput(t);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => { setRecording(false); toast.error("Mic error — dubara try karo."); };
    recRef.current = rec; setRecording(true); rec.start();
  };

  return (
    <Layout title="AI Assistant">
      <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-9rem)]">
        <div className="flex items-center justify-between gap-2 mb-3 relative">
          <button onClick={() => { setShowSessions((v) => !v); loadSessions(); }} data-testid="assistant-history-btn" className="px-3 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold transition-colors duration-200 flex items-center gap-1.5"><History size={13} />Chat history</button>
          <button onClick={newChat} data-testid="assistant-new-chat" className="px-3 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold transition-colors duration-200 flex items-center gap-1.5"><Plus size={13} />New chat</button>
          {showSessions && (
            <div className="absolute top-10 left-0 z-20 w-72 max-h-80 overflow-y-auto surface border border-[color:var(--border-subtle)] p-1" data-testid="sessions-dropdown">
              {sessions.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No past chats yet.</div>}
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => switchSession(s.session_id)} data-testid={`session-${s.session_id}`} className={`w-full text-left px-3 py-2 text-xs hover:bg-[color:var(--border-subtle)] transition-colors ${s.session_id === sessionId ? "text-gold" : "text-slate-300"}`}>
                  <div className="truncate">{s.title}</div>
                  <div className="text-[10px] text-slate-500">{(s.last_at || "").slice(0, 16).replace("T", " ")} · {s.count} msgs</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="assistant-messages">
          {msgs.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.role}-${i}`}>
              <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-[color:var(--border-subtle)]" : "bg-gold text-black"}`}>
                {m.role === "user" ? <User size={16} /> : <Bot size={16} />}
              </div>
              <div className={`max-w-[80%] px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[color:var(--border-subtle)] text-white" : "surface text-slate-100"}`}>
                {m.text}
                {m.action && (
                  <div className="mt-3 flex gap-2" data-testid={`action-${i}`}>
                    <button onClick={() => confirm(i, m.action)} data-testid={`confirm-action-${i}`} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1"><CheckCircle2 size={13} />Confirm & Run</button>
                    <button onClick={() => cancel(i)} className="px-3 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-400 hover:text-red-400 flex items-center gap-1"><X size={13} />Cancel</button>
                  </div>
                )}
                {m.done && <div className="mt-1 text-[11px] text-emerald-400">✓ done</div>}
              </div>
            </div>
          ))}
          {loading && <div className="flex gap-3"><div className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center"><Bot size={16} /></div><div className="surface px-4 py-2.5"><Loader2 size={16} className="animate-spin text-gold" /></div></div>}
          <div ref={endRef} />
        </div>

        {msgs.length <= 1 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} onClick={() => ask(s)} data-testid={`suggestion-${i}`} className="px-3 py-1.5 text-xs border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold transition-colors duration-200 flex items-center gap-1"><Sparkles size={12} />{s}</button>
            ))}
          </div>
        )}

        <div className="flex gap-2 border-t border-[color:var(--border-subtle)] pt-3 items-center">
          <button onClick={() => setLang((l) => (l === "en-IN" ? "hi-IN" : "en-IN"))} data-testid="assistant-lang-toggle" title="Voice language" className="px-2.5 py-2.5 text-xs border border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold transition-colors">{lang === "en-IN" ? "EN" : "HI"}</button>
          <button onClick={toggleMic} data-testid="assistant-mic" title="Speak" className={`px-3 py-2.5 border transition-colors ${recording ? "border-red-500 text-red-400 animate-pulse" : "border-[color:var(--border-subtle)] text-slate-300 hover:border-gold hover:text-gold"}`}><Mic size={16} /></button>
          <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder={recording ? "Sun raha hun… boliye" : "Leads, signals ya performance ke baare me poochho…"} className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white focus:border-gold focus:outline-none" />
          <button onClick={() => ask()} disabled={loading || !input.trim()} data-testid="assistant-send" className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60"><Send size={16} /></button>
        </div>
      </div>
    </Layout>
  );
}
